import { ref } from 'vue'
import { useUserStore } from '@/stores/user'
import Swal from 'sweetalert2'

export function useGeolocation($googleMapsLoader) {
  const position = ref(null)
  const address = ref(null)
  const error = ref(null)

  const maxRetries = 5
  let currentRetries = 0

  // Obtener la ubicación actual con reintentos
  async function getCurrentPosition() {
    if (!navigator.geolocation) {
      error.value = 'Geolocalización no disponible en este navegador.'
      throw error.value
    }

    while (currentRetries < maxRetries) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos),
            (err) => reject(err)
          )
        })

        position.value = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        }
        currentRetries = 0
        return position.value
      } catch (err) {
        currentRetries++
        if (currentRetries >= maxRetries) {
          error.value = err.message || 'No se pudo obtener la ubicación'
          Swal.fire({
            title: 'No se pudo obtener tu ubicación',
            text: `Intentamos ${maxRetries} veces sin éxito. Verifica tus permisos de ubicación o recarga el sitio.`,
            icon: 'error',
            confirmButtonText: 'Entendido'
          })
          throw error.value
        }
      }
    }
  }

  // Verifica permisos de geolocalización
  async function checkGeolocationPermission() {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      Swal.fire({
        title: 'Geolocalización no soportada',
        text: 'Tu navegador no soporta la API de permisos de geolocalización.',
        icon: 'warning',
        confirmButtonText: 'Entendido'
      })
      return null
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' })
      if (status.state === 'granted') return true
      if (status.state === 'prompt') return true
      if (status.state === 'denied') return false
    } catch {
      return null
    }

    return null
  }

  // Obtener dirección desde posición
  async function getAddressFromPosition(pos) {
    if (!$googleMapsLoader) {
      error.value = 'Google Maps Loader no disponible'
      return null
    }

    let google
    try {
      google = await $googleMapsLoader.load()
    } catch (e) {
      error.value = 'Error cargando Google Maps'
      console.error('Error al cargar Google Maps:', e)
      Swal.fire({
        title: 'Error al cargar mapas',
        text: 'No se pudo cargar Google Maps. Revisa tu conexión o intenta más tarde.',
        icon: 'error'
      })
      return null
    }

    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ location: pos }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          address.value = {
            formatted_address: results[0].formatted_address,
            address_components: results[0].address_components,
            address_format: getFormatAddressData(results[0].address_components)
          }
          resolve(address.value)
        } else {
          error.value = 'No se pudo obtener la dirección'
          console.error('Geocoder failed due to:', status)

          if (status === 'REQUEST_DENIED') {
            Swal.fire({
              title: 'Versión desactualizada del sitio',
              html: 'Parece que estás usando una versión antigua del sitio sin permisos para usar Google Maps.<br><br><strong>Solución:</strong> Recarga sin caché (Ctrl + Shift + R o Cmd + Shift + R) o borra la caché del navegador.',
              icon: 'error',
              confirmButtonText: 'Entendido'
            })
          }

          reject(error.value)
        }
      })
    })
  }

  // Formatear dirección
  function getFormatAddressData(address_components) {
    const address = {
      street_number: '',
      route: '',
      locality: '',
      sublocality: '',
      political: '',
      sublocality_level_1: '',
      neighborhood: '',
      postal_code: ''
    }

    address_components.forEach((component) => {
      const componentType = component.types[0]
      if (address[componentType] !== undefined) {
        address[componentType] = component.long_name
      }
    })

    return {
      city: address.locality,
      street: address.route,
      num: address.street_number,
      suburb:
        address.sublocality ||
        address.political ||
        address.sublocality_level_1 ||
        address.neighborhood,
      zip: address.postal_code
    }
  }

  // Guardar ubicación completa en el user store
  async function getLocationAndSaveToUserStore() {
    try {
      const userStore = useUserStore()
      const pos = await getCurrentPosition()
      const addr = await getAddressFromPosition(pos)

      if (!pos || !addr) throw new Error('Ubicación o dirección no disponible')

      userStore.updateUserLocation({
        location: pos,
        ...addr
      })
    } catch (e) {
      console.error('Error al obtener o guardar la ubicación:', e)
      useUserStore().updateUserLocation(false)
    }
  }

  // Calcular sucursal más cercana
  function getNearestOffice(userLocation, offices) {
    if (!userLocation || !offices || offices.length === 0) return null

    function haversineDistance(lat1, lng1, lat2, lng2) {
      const toRad = (x) => (x * Math.PI) / 180
      const R = 6371
      const dLat = toRad(lat2 - lat1)
      const dLng = toRad(lng2 - lng1)
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    }

    let nearest = offices[0]
    let minDist = haversineDistance(
      userLocation.lat,
      userLocation.lng,
      offices[0].location.lat,
      offices[0].location.lng
    )

    for (let i = 1; i < offices.length; i++) {
      const dist = haversineDistance(
        userLocation.lat,
        userLocation.lng,
        offices[i].location.lat,
        offices[i].location.lng
      )
      if (dist < minDist) {
        minDist = dist
        nearest = offices[i]
      }
    }
    return nearest
  }

  return {
    state: {
      position,
      address,
      error
    },
    methods: {
      getCurrentPosition,
      getAddressFromPosition,
      getLocationAndSaveToUserStore,
      checkGeolocationPermission,
      getNearestOffice
    }
  }
}
